// File: webelieve-frontend/src/features/contacts/components/tabs/DocumentsTab.tsx
// Real document management for contact profiles — fetches from DMS + intake APIs

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Contact } from '../../../../config/api';
import { courtIntelligenceApi } from '@/features/matters/api/courtIntelligenceApi';

interface DocumentRecord {
  id: string;
  name: string;
  type: string;         // file extension
  mimeType: string;
  size: number;         // bytes
  uploadDate: string;
  category: string;
  source: 'dms' | 'intake';
  processingStatus?: string;
  matterId?: string | null;
  documentId: string;   // backend ID for download/view
  courtFormId?: string | null;
  courtFormNumber?: string | null;
  courtBindingId?: string | null;
}

interface DocumentsTabProps {
  contact: Contact;
}

function formatFileSize(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || 'unknown';
}

function getFileIcon(ext: string): string {
  switch (ext) {
    case 'pdf': return '📄';
    case 'doc':
    case 'docx': return '📝';
    case 'xls':
    case 'xlsx': return '📊';
    case 'zip':
    case 'rar': return '📦';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp': return '🖼️';
    case 'mp4':
    case 'mov':
    case 'avi': return '🎬';
    case 'mp3':
    case 'wav': return '🎵';
    default: return '📄';
  }
}

function getCategoryFromMime(mime: string, name: string): string {
  if (mime.startsWith('image/')) return 'Evidence';
  if (mime.startsWith('video/')) return 'Evidence';
  if (mime.startsWith('audio/')) return 'Evidence';
  const lowerName = name.toLowerCase();
  if (lowerName.includes('contract') || lowerName.includes('engagement') || lowerName.includes('nda')) return 'Contracts';
  if (lowerName.includes('letter') || lowerName.includes('agreement')) return 'Legal';
  if (lowerName.includes('invoice') || lowerName.includes('receipt') || lowerName.includes('bill')) return 'Financial';
  return 'Other';
}

// Auth helpers
function getAuthToken(): string | null {
  return (window as any).keycloak?.token
    || sessionStorage.getItem('token')
    || localStorage.getItem('access_token')
    || null;
}

function getRealmId(): string | null {
  return localStorage.getItem('webelieveSubscriberId')
    || (window as any).keycloak?.realm
    || null;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ contact }) => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isUploading, setIsUploading] = useState(false);
  const [isAddingCourtForm, setIsAddingCourtForm] = useState(false);
  const [addCourtFormNumber, setAddCourtFormNumber] = useState('');
  const [addCourtFormError, setAddCourtFormError] = useState<string | null>(null);
  const [fillingDocumentIds, setFillingDocumentIds] = useState<Set<string>>(new Set());

  const categories = ['all', 'Contracts', 'Evidence', 'Legal', 'Financial', 'Other'];

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    const token = getAuthToken();
    const realmId = getRealmId();
    const merged: DocumentRecord[] = [];
    const courtBindingByDocumentId = new Map<
      string,
      { binding_id: string; court_form_id: string; source_form_number: string }
    >();

    try {
      const contextBindings = await courtIntelligenceApi.listContextBindings({
        context_type: 'client',
        client_id: contact.id,
      });
      for (const binding of contextBindings) {
        courtBindingByDocumentId.set(binding.document_id, {
          binding_id: binding.binding_id,
          court_form_id: binding.court_form_id,
          source_form_number: binding.source_form_number,
        });
      }
    } catch {
      // Non-blocking: client documents should still render without binding metadata.
    }

    // Fetch DMS documents for this client
    try {
      const params = new URLSearchParams();
      params.set('client_id', contact.id);
      const res = await fetch(`/api/v1/dms/documents?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(realmId ? { 'X-Realm-ID': realmId } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        const docs = data.documents || data.data?.documents || [];
        for (const doc of docs) {
          const name = doc.title || doc.original_name || doc.file_name || 'Untitled';
          const mime = doc.mime_type || doc.mimeType || 'application/octet-stream';
          const documentId = doc.document_id || doc.id;
          const binding = courtBindingByDocumentId.get(documentId);
          merged.push({
            id: documentId,
            documentId,
            name,
            type: getExtension(name),
            mimeType: mime,
            size: doc.file_size || doc.fileSize || 0,
            uploadDate: doc.created_at || doc.createdAt || doc.uploadedAt || new Date().toISOString(),
            category: getCategoryFromMime(mime, name),
            source: 'dms',
            matterId: doc.matter_id || doc.matterId || null,
            courtFormId: binding?.court_form_id || null,
            courtFormNumber: binding?.source_form_number || null,
            courtBindingId: binding?.binding_id || null,
          });
        }
      }
    } catch (err) {
      console.warn('[DocumentsTab] Failed to fetch DMS documents:', err);
    }

    // Fetch intake-uploaded documents
    try {
      const res = await fetch(`/api/v1/intake/leads/${contact.id}/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(realmId ? { 'X-Realm-ID': realmId } : {}),
        },
      });
      if (res.ok) {
        const json = await res.json();
        const docs = json.data?.documents || [];
        for (const doc of docs) {
          // Skip if already in DMS list (deduplicate by filename)
          const alreadyExists = merged.some(m => m.name === doc.fileName && Math.abs(m.size - (doc.fileSize || 0)) < 100);
          if (alreadyExists) continue;

          const name = doc.fileName || 'Untitled';
          const mime = doc.mimeType || 'application/octet-stream';
          merged.push({
            id: doc.documentId || `intake-${Date.now()}`,
            documentId: doc.documentId,
            name,
            type: getExtension(name),
            mimeType: mime,
            size: doc.fileSize || 0,
            uploadDate: doc.uploadedAt || new Date().toISOString(),
            category: getCategoryFromMime(mime, name),
            source: 'intake',
            processingStatus: doc.processingStatus,
            matterId: doc.matterId || null,
          });
        }
      }
    } catch (err) {
      console.warn('[DocumentsTab] Failed to fetch intake documents:', err);
    }

    // Sort by date descending
    merged.sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime());

    if (merged.length === 0 && !error) {
      // Not an error — just no documents yet
    }

    setDocuments(merged);
    setLoading(false);
  }, [contact.id]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const token = getAuthToken();
    const realmId = getRealmId();
    const authHeaders: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
      ...(realmId ? { 'X-Realm-ID': realmId } : {}),
    };

    for (const file of Array.from(files)) {
      try {
        // Step 1: Upload file binary to DMS upload endpoint
        const formData = new FormData();
        formData.append('file', file);

        const uploadRes = await fetch('/api/v1/dms/uploads', {
          method: 'POST',
          headers: authHeaders,
          body: formData,
        });

        if (!uploadRes.ok) {
          const body = await uploadRes.json().catch(() => null);
          console.error('[DocumentsTab] File upload failed:', body?.description || uploadRes.statusText);
          continue;
        }

        const uploadedFile = await uploadRes.json();

        // Step 2: Create document record linked to this client
        const docRes = await fetch('/api/v1/dms/documents', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify({
            title: file.name,
            client_id: contact.id,
            uploadedFile,
          }),
        });

        if (!docRes.ok) {
          const body = await docRes.json().catch(() => null);
          console.error('[DocumentsTab] Document record creation failed:', body?.description || docRes.statusText);
        }
      } catch (err) {
        console.error('[DocumentsTab] Upload error:', err);
      }
    }

    setIsUploading(false);
    event.target.value = '';
    // Refresh document list
    await fetchDocuments();
  };

  const handleDownload = useCallback(async (doc: DocumentRecord) => {
    if (doc.source === 'dms' && doc.documentId) {
      navigate(`/dms/editor/${encodeURIComponent(doc.documentId)}`);
      return;
    }

    const token = getAuthToken();
    const realmId = getRealmId();
    try {
      const res = await fetch(`/api/v1/dms/documents/${doc.documentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(realmId ? { 'X-Realm-ID': realmId } : {}),
        },
      });
      if (res.ok) {
        // For now, open in new tab — download endpoint may differ
        window.open(`/api/v1/dms/documents/${doc.documentId}`, '_blank');
      }
    } catch (err) {
      console.error('[DocumentsTab] Download error:', err);
    }
  }, [navigate]);

  const handleAddCourtForm = useCallback(async () => {
    const normalizedFormNumber = String(addCourtFormNumber || '').trim().toUpperCase();
    if (!normalizedFormNumber) {
      setAddCourtFormError('Enter a court form number (for example: FL-100).');
      return;
    }

    setIsAddingCourtForm(true);
    setAddCourtFormError(null);

    try {
      const lookup = await courtIntelligenceApi.lookupFormUrls([normalizedFormNumber]);
      const exact = lookup[normalizedFormNumber];
      const fallback = Object.values(lookup)[0];
      const match = exact || fallback;

      if (!match?.id) {
        setAddCourtFormError(`No active catalog match found for ${normalizedFormNumber}.`);
        return;
      }

      const materialized = await courtIntelligenceApi.materializeToDms(match.id, {
        context_type: 'client',
        client_id: contact.id,
        title: `${normalizedFormNumber} - ${match.form_name || 'Court Form'}`,
      });

      setAddCourtFormNumber('');
      await fetchDocuments();
      const versionQuery = materialized.document.latest_version_id
        ? `?versionId=${encodeURIComponent(materialized.document.latest_version_id)}`
        : '';
      navigate(`/dms/editor/${encodeURIComponent(materialized.document.document_id)}${versionQuery}`);
    } catch {
      setAddCourtFormError('Unable to add court form to this client.');
    } finally {
      setIsAddingCourtForm(false);
    }
  }, [addCourtFormNumber, contact.id, fetchDocuments, navigate]);

  const handleFillCourtForm = useCallback(async (doc: DocumentRecord) => {
    if (!doc.courtFormId) return;

    setFillingDocumentIds((prev) => {
      const next = new Set(prev);
      next.add(doc.documentId);
      return next;
    });
    setAddCourtFormError(null);

    try {
      const result = await courtIntelligenceApi.fillToDms(doc.courtFormId, {
        context_type: 'client',
        client_id: contact.id,
        version_description: doc.courtFormNumber
          ? `Filled ${doc.courtFormNumber} from Client Documents tab`
          : 'Filled court form from Client Documents tab',
      });

      await fetchDocuments();
      navigate(
        `/dms/editor/${encodeURIComponent(result.document.document_id)}?versionId=${encodeURIComponent(result.version.version_id)}`,
      );
    } catch {
      setAddCourtFormError(
        doc.courtFormNumber
          ? `Unable to fill ${doc.courtFormNumber} in DMS.`
          : 'Unable to fill this court form in DMS.',
      );
    } finally {
      setFillingDocumentIds((prev) => {
        const next = new Set(prev);
        next.delete(doc.documentId);
        return next;
      });
    }
  }, [contact.id, fetchDocuments, navigate]);

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = !searchTerm
      || doc.name.toLowerCase().includes(searchTerm.toLowerCase())
      || doc.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const totalSize = documents.reduce((acc, doc) => acc + (doc.size || 0), 0);

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold">Documents</h3>

      {/* Upload Section */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="cursor-pointer w-full sm:w-auto">
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.docx,.doc,.xlsx,.xls,.jpg,.jpeg,.png,.gif,.zip,.mp4,.mov,.mp3,.wav"
                disabled={isUploading}
              />
              <div className={`px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors w-full sm:w-auto text-center ${isUploading ? 'opacity-50 cursor-wait' : ''}`}>
                {isUploading ? 'Uploading...' : 'Upload Files'}
              </div>
            </label>
            <span className="text-sm text-gray-500 dark:text-gray-400 w-full sm:w-auto text-center sm:text-left">
              Supported: PDF, DOCX, XLSX, JPG, PNG, ZIP
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={addCourtFormNumber}
              onChange={(e) => setAddCourtFormNumber(e.target.value)}
              placeholder="Add court form number (e.g., FL-100)"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              type="button"
              onClick={handleAddCourtForm}
              disabled={isAddingCourtForm}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isAddingCourtForm
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-lex-accent-soft text-lex-accent hover:bg-lex-accent-soft/80'
              }`}
            >
              {isAddingCourtForm ? 'Adding...' : 'Add Court Form'}
            </button>
          </div>
        </div>
      </div>

      {addCourtFormError && (
        <div className="text-sm text-red-600 dark:text-red-400">
          {addCourtFormError}
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search documents..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full sm:w-auto"
        >
          {categories.map(category => (
            <option key={category} value={category}>
              {category === 'all' ? 'All Categories' : category}
            </option>
          ))}
        </select>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="text-center py-8">
          <p className="text-sm text-red-500 mb-2">{error}</p>
          <button onClick={fetchDocuments} className="text-sm text-lex-accent hover:underline">Retry</button>
        </div>
      )}

      {/* Documents List */}
      {!loading && !error && (
        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between text-sm text-gray-500 dark:text-gray-400 gap-y-1 gap-x-4">
            <span>{filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}</span>
            <span>Total: {formatFileSize(totalSize)}</span>
          </div>

          <div className="space-y-2">
            {filteredDocuments.map((doc) => (
              <div key={doc.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors gap-y-2 gap-x-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl flex-shrink-0">{getFileIcon(doc.type)}</span>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white break-words">{doc.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 break-words">
                      {formatFileSize(doc.size)} • {doc.category} • {new Date(doc.uploadDate).toLocaleDateString()}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                        doc.source === 'intake'
                          ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                          : 'bg-lex-accent-soft text-lex-accent'
                      }`}>
                        {doc.source === 'intake' ? 'Intake Upload' : 'DMS'}
                      </span>
                      {doc.processingStatus && doc.processingStatus !== 'completed' && (
                        <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded-full text-[10px] font-medium">
                          {doc.processingStatus}
                        </span>
                      )}
                      {doc.matterId && (
                        <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-full text-[10px] font-medium">
                          Linked to Matter
                        </span>
                      )}
                      {doc.courtFormId && (
                        <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-[10px] font-medium">
                          Court Form{doc.courtFormNumber ? ` ${doc.courtFormNumber}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  {doc.courtFormId && (
                    <button
                      type="button"
                      onClick={() => handleFillCourtForm(doc)}
                      disabled={fillingDocumentIds.has(doc.documentId)}
                      className={`px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                        fillingDocumentIds.has(doc.documentId)
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover'
                      }`}
                      title="Fill to new DMS version"
                    >
                      {fillingDocumentIds.has(doc.documentId) ? 'Filling...' : 'Fill'}
                    </button>
                  )}
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    title="Open in DMS"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredDocuments.length === 0 && (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
              <span className="text-3xl mb-3 block">📁</span>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {documents.length === 0
                  ? 'No documents yet'
                  : 'No documents matching your search'}
              </p>
              {documents.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Upload files or submit an intake form to add documents
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;
